// SearchableSelect — a type-to-filter combobox that drops in for a plain
// <select> when the option list is long (chemicals, blocks, growers, recipes…).
// Looks like a `field` input when closed; opens a search box + filtered list.
// Keyboard: ↑/↓ to move, Enter to pick, Esc to close. Click-outside closes.
//
// Usage mirrors a <select> but onChange gives the value directly:
//   <SearchableSelect value={id} onChange={setId} label="Product"
//     options={chemicals.map(c => ({ value: c.chemical_id, label: c.product_name, detail: 'Insecticide · 40 L in stock' }))}
//     placeholder="— pick a chemical —" clearLabel="— none —" />
//
// HOW IT OPENS (SPRAY-FERT-UX-AUDIT-2026-09-16, "the drop-downs are cut off"):
// the list used to be an `absolute` box under the trigger. Inside <Modal>'s
// scrolling body that box was clipped at the footer and never flipped — 0 of
// 28 products visible when opened from the lower half of New spray — and
// focusing the search box raised an Android keyboard straight over it.
//   • Phone: a full-screen sheet sized to the VISIBLE viewport, search box at
//     the top and NOT focused on open. The keyboard comes up only when the
//     person taps Search, and the list then shrinks above it instead of hiding
//     under it.
//   • Tablet and mouse: a popover positioned `fixed` against the trigger, so
//     no scrolling ancestor can clip it (Modal has no transform/filter, so
//     fixed escapes it); it opens upward when the room below is short. On a
//     touch tablet its rows are 44px and, as on the phone, the search box is
//     NOT focused on open — the list is what the person came for.
// Which one is useDeviceClass()'s answer, read when the list opens. It used to
// be `pointer: coarse` alone, so a 1024 iPad got a phone's sheet over its whole
// screen for a 12-row list (farm audit SP-07 / X-11).
// Both stay INSIDE this component's DOM subtree — no portal — so a Modal's
// focus trap and aria-modal keep treating them as part of the dialog.
//
// Options carry an optional `detail` line (stock, tank size, worker number)
// that wraps instead of being cut off with "…", and is searched too.
//
// `required` takes part in <Modal>'s jump-to-required: Save with nothing
// picked jumps to the trigger, rings it red and refuses to submit — the first
// custom control on the `requiredWidgetProps` convention (src/lib/requiredFields).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search, Check, X } from 'lucide-react'
import clsx from 'clsx'
import { useDeviceClass, type DeviceClass } from '@/hooks/useDeviceClass'
import { visibleViewport, useViewportListener } from '@/hooks/useVisualViewportInset'
import { requiredWidgetProps } from '@/lib/requiredFields'

export interface SearchableOption {
  value:   string
  label:   string
  /** A quieter second line — stock, tank size, worker number. Searched too. */
  detail?: string
}

/** Where the mouse popover goes. Pure, so it is testable without a browser. */
export interface PopoverPlacement { top?: number; bottom?: number; left: number; width: number; maxList: number }

const GAP = 4          // trigger ↔ popover
const EDGE = 8         // popover ↔ viewport edge
const SEARCH_ROW = 44  // the search row's height
const LIST_MAX = 280   // the list's own cap when there is room
const LIST_MIN = 96    // never smaller than ~2 rows, even in a cramped corner

export function popoverPlacement(
  rect: { top: number; bottom: number; left: number; width: number },
  viewportHeight: number,
): PopoverPlacement {
  const below = viewportHeight - rect.bottom - GAP - EDGE
  const above = rect.top - GAP - EDGE
  const wanted = LIST_MAX + SEARCH_ROW
  const up = below < wanted && above > below
  const room = (up ? above : below) - SEARCH_ROW
  const maxList = Math.max(LIST_MIN, Math.min(LIST_MAX, room))
  const side = up ? { bottom: viewportHeight - rect.top + GAP } : { top: rect.bottom + GAP }
  return { ...side, left: rect.left, width: rect.width, maxList }
}

/** Case-insensitive match on the label or the detail line. */
export function filterOptions(options: SearchableOption[], query: string): SearchableOption[] {
  const s = query.trim().toLowerCase()
  if (!s) return options
  return options.filter(o => o.label.toLowerCase().includes(s) || !!o.detail?.toLowerCase().includes(s))
}

/** The layout classes of a native-select `className` (flex-1, w-full, min-w-0…)
 *  that must sit on the wrapper, not the trigger, for a drop-in swap to keep
 *  its place in a flex row. */
export function layoutClasses(className?: string): string {
  return (className ?? '').split(/\s+/).filter(c => /^(flex-|flex$|grow|shrink|w-|min-w-|max-w-|basis-)/.test(c)).join(' ')
}

/** Row height per device: the phone sheet's 48px, a finger's 44px floor in
 *  the tablet popover (explicit — not left to the `pointer: coarse` media
 *  query), and the compact mouse row. */
const ROW: Record<DeviceClass, string> = {
  phone:   'min-h-[48px] py-3',
  tablet:  'min-h-[44px] py-2.5',
  desktop: 'tap-target-y py-3 sm:py-1.5',
}

function OptionRows({ options, value, active, device, clearLabel, onPick, onHover, noMatch }: {
  options: SearchableOption[]; value: string; active: number; device: DeviceClass
  clearLabel?: string; onPick: (v: string) => void; onHover: (i: number) => void
  /** An action offered right in the "No matches" row ("+ New product"). */
  noMatch?: { label: string; run: () => void }
}) {
  const row = ROW[device]
  return (
    <>
      {clearLabel && (
        <button type="button" onClick={() => onPick('')}
          className={clsx('w-full text-left px-3 text-sm text-muted hover:bg-gray-800', row)}>
          {clearLabel}
        </button>
      )}
      {options.length === 0 && <p className="px-3 py-2 text-xs text-muted">No matches</p>}
      {options.length === 0 && noMatch && (
        <button type="button" onClick={noMatch.run}
          className={clsx('w-full text-left px-3 text-sm text-blue-300 hover:bg-gray-800', row)}>
          {noMatch.label}
        </button>
      )}
      {options.map((o, i) => (
        <button type="button" key={o.value} data-option-index={i}
          onClick={() => onPick(o.value)}
          onMouseEnter={() => onHover(i)}
          className={clsx('w-full block text-left px-3 text-sm', row,
            i === active ? 'surface text-white' : 'text-gray-200 hover:bg-gray-800')}>
          <span className="flex items-start gap-2">
            <span className="min-w-0 flex-1 break-words">
              <span className="block">{o.label}</span>
              {o.detail && <span className="block text-xs text-muted">{o.detail}</span>}
            </span>
            {o.value === value && <Check size={14} className="text-emerald-400 shrink-0 mt-0.5" />}
          </span>
        </button>
      ))}
    </>
  )
}

/** The keyboard's next highlighted row: ↑/↓ move within the list, anything
 *  else leaves it where it is. */
export function nextActive(key: string, active: number, count: number): number {
  if (key === 'ArrowDown') return Math.min(active + 1, count - 1)
  if (key === 'ArrowUp') return Math.max(active - 1, 0)
  return active
}

/** Click-outside (popover) and Escape both close the list. A click outside
 *  only closes — focus goes where the person clicked — while Escape closes and
 *  hands focus back to the field. Escape is captured at the document and
 *  stopped there, so a Modal's own Escape handler never sees it and the form
 *  around the list stays open. */
function useDismiss(
  open: boolean, rootRef: React.RefObject<HTMLDivElement>,
  dismiss: () => void, close: () => void,
) {
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) dismiss()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, rootRef, dismiss, close])
}

/** On open, focus the search box with a mouse — a keyboard user types at
 *  once — but the PANEL itself on touch (phone sheet or tablet popover):
 *  focusing the input is what raised the keyboard over the list it had just
 *  opened. */
function useOpenFocus(open: boolean, target: () => HTMLElement | null, reset: () => void) {
  useEffect(() => {
    if (!open) return
    reset()
    const id = setTimeout(() => target()?.focus(), 0)
    return () => clearTimeout(id)
  }, [open, target, reset])
}

function SearchRow({ q, setQ, label, inputRef, onKeyDown }: {
  q: string; setQ: (v: string) => void; label?: string
  inputRef: React.RefObject<HTMLInputElement>; onKeyDown: (e: React.KeyboardEvent) => void
}) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b hairline">
      <Search size={13} className="text-muted shrink-0" />
      <input ref={inputRef} value={q} onKeyDown={onKeyDown}
        onChange={e => setQ(e.target.value)}
        aria-label={label ? `Search ${label.toLowerCase()}` : 'Search the list'}
        placeholder="Search…"
        className="flex-1 min-w-0 bg-transparent text-base text-white outline-none placeholder-gray-500" />
      {q && (
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setQ('')}
          aria-label="Clear the search"
          className="tap-target p-2 -m-1.5 text-muted hover:text-white shrink-0"><X size={13} /></button>
      )}
    </div>
  )
}

/** Phone: the whole visible viewport, heading + Close, search, then the list.
 *  Until the sheet has measured the visible viewport (`view`), `top-0 h-dvh`
 *  hold it — a CLASS, so PostCSS gives it a `100vh` twin: an inline `100dvh`
 *  is rejected outright by the floor's Chrome 86 handsets (no dvh before
 *  Chrome 108), and the first frame had no height. */
function SheetPanel({ panelRef, label, view, onClose, search, rows }: {
  panelRef: React.RefObject<HTMLDivElement>; label?: string
  view: { top: number; height: number } | null; onClose: () => void
  search: React.ReactNode; rows: React.ReactNode
}) {
  const title = label ?? 'Choose'
  return (
    <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={label ?? 'Choose an option'}
      data-theme="dark"
      className="fixed inset-x-0 top-0 h-dvh z-[70] flex flex-col surface-sunk outline-none motion-safe:animate-dialog-in"
      style={view ?? undefined}>
      <div className="flex items-center gap-2 pl-4 pr-2 py-1 border-b hairline">
        <p className="flex-1 min-w-0 truncate text-base font-semibold text-white">{title}</p>
        <button type="button" onClick={onClose} aria-label="Close the list"
          className="w-11 h-11 flex items-center justify-center rounded-lg text-muted hover:text-white">
          <X size={20} />
        </button>
      </div>
      {search}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-1">{rows}</div>
    </div>
  )
}

/** Tablet and mouse: fixed against the trigger, so no scrolling ancestor can
 *  clip it. Focusable (tabIndex -1) so a touch open can land focus here rather
 *  than in the search box; overscroll-contain so a finger flicking the list to
 *  its end does not scroll the form behind it out from under the field. */
function PopoverPanel({ panelRef, place, search, rows }: {
  panelRef: React.RefObject<HTMLDivElement>; place: PopoverPlacement
  search: React.ReactNode; rows: React.ReactNode
}) {
  return (
    <div ref={panelRef} tabIndex={-1} data-theme="dark"
      className="fixed z-[70] surface-sunk border hairline-strong rounded-lg shadow-xl overflow-hidden outline-none motion-safe:animate-dialog-in"
      style={{ top: place.top, bottom: place.bottom, left: place.left, width: place.width }}>
      {search}
      <div className="overflow-y-auto overscroll-contain py-1" style={{ maxHeight: place.maxList }}>{rows}</div>
    </div>
  )
}

/** The closed field. The flex row lives on an inner <span>, never on the
 *  <button> itself: WebKit ignores justify-content on a flex <button> and
 *  centres its anonymous content box instead, so an over-long label spills out
 *  both sides and gets chopped mid-word rather than ellipsised. Same reason for
 *  min-w-0 — a flex item's automatic minimum size refuses to shrink below
 *  max-content on engines that skip the overflow-hidden carve-out. */
function Trigger({ triggerRef, text, placeholder, open, disabled, label, className, onClick, required, empty }: {
  triggerRef: React.RefObject<HTMLButtonElement>; text: string | undefined; placeholder: string
  open: boolean; disabled: boolean; label?: string; className?: string; onClick: () => void
  required: boolean; empty: boolean
}) {
  const shown = text ?? placeholder
  return (
    <button ref={triggerRef} type="button" disabled={disabled} onClick={onClick}
      {...requiredWidgetProps({ required, empty, caption: label })}
      aria-expanded={open}
      aria-label={label ? `${label}: ${shown}` : undefined}
      className={clsx('field block text-left', className, disabled && 'opacity-50 cursor-not-allowed')}>
      <span className="flex items-center justify-between gap-2">
        <span className={clsx('min-w-0 truncate', text == null && 'text-muted')}>{shown}</span>
        <ChevronDown size={14} className="text-muted shrink-0" />
      </span>
    </button>
  )
}

export function SearchableSelect({
  value, onChange, options,
  placeholder = 'Select…',
  clearLabel,
  disabled = false,
  className,
  rootClassName,
  label,
  noMatchAction,
  required = false,
  initialQuery = '',
}: {
  value:        string
  onChange:     (value: string) => void
  options:      SearchableOption[]
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string
  /** When set, adds a top row that clears the selection back to ''. */
  clearLabel?:  string
  disabled?:    boolean
  className?:   string
  /** Classes for the wrapper div (flex-1, w-full) when the field sits in a flex row. */
  rootClassName?: string
  /** What is being picked ("Sprayer", "Product") — the phone sheet's heading
   *  and part of the trigger's accessible name. */
  label?:       string
  /** Offered in the list when the search finds nothing, so the next step is
   *  where the person is already looking ("+ New product"). Gets what they
   *  typed; the list closes first. */
  noMatchAction?: { label: string; onSelect: (query: string) => void }
  /** Inside a submitting <Modal>: Save with nothing picked ('' — the value,
   *  not whether it matches a loaded option, so a seeded edit is never
   *  refused while its options load) jumps here and says "Required". */
  required?:    boolean
  /** What the search box holds each time the list opens ('' by default). A
   *  scan hands over the first word it read, so the list opens on the likely
   *  products; the person clears or retypes it as for any search. */
  initialQuery?: string
}) {
  const device = useDeviceClass()
  const [open, setOpen]     = useState(false)
  // The class the list OPENED as: a keyboard or split-screen change mid-pick
  // must not swap a popover for a sheet under the person's finger.
  const [openedAs, setOpenedAs] = useState<DeviceClass>('desktop')
  const [q, setQ]           = useState('')
  const [active, setActive] = useState(0)
  const [place, setPlace]   = useState<PopoverPlacement | null>(null)
  const [view, setView]     = useState<{ top: number; height: number } | null>(null)
  const rootRef    = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef   = useRef<HTMLInputElement>(null)
  const panelRef   = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => filterOptions(options, q), [options, q])

  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])
  const toggle = () => {
    setOpenedAs(device)
    setOpen(o => !o)
  }
  const sheet = openedAs === 'phone'
  const touch = openedAs !== 'desktop'
  const reset = useCallback(() => { setQ(initialQuery); setActive(0) }, [initialQuery])
  const focusTarget = useCallback(() => (touch ? panelRef.current : inputRef.current), [touch])
  useOpenFocus(open, focusTarget, reset)
  const dismiss = useCallback(() => setOpen(false), [])
  useDismiss(open, rootRef, dismiss, close)

  const placePopover = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect()
    if (!r) return
    const v = visibleViewport()
    setPlace(popoverPlacement(r, v.top + v.height))
  }, [])
  const sizeSheet = useCallback(() => setView(visibleViewport()), [])
  useViewportListener(open && !sheet, placePopover)
  useViewportListener(open && sheet, sizeSheet)

  // Keep the keyboard-highlighted option in view.
  useEffect(() => {
    if (!open) return
    panelRef.current?.querySelector<HTMLElement>(`[data-option-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [active, open])

  const pick = (v: string) => { onChange(v); close() }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const o = filtered[active]
      if (o) pick(o.value)
      return
    }
    const next = nextActive(e.key, active, filtered.length)
    if (next !== active) { e.preventDefault(); setActive(next) }
  }
  const onQuery = (v: string) => { setQ(v); setActive(0) }

  const search = <SearchRow q={q} setQ={onQuery} label={label} inputRef={inputRef} onKeyDown={onKeyDown} />
  const noMatch = noMatchAction
    ? { label: noMatchAction.label, run: () => { const typed = q.trim(); close(); noMatchAction.onSelect(typed) } }
    : undefined
  const rows = (
    <OptionRows options={filtered} value={value} active={active} device={openedAs}
      clearLabel={clearLabel} onPick={pick} onHover={setActive} noMatch={noMatch} />
  )
  const popover = !sheet && place

  return (
    <div ref={rootRef} className={clsx('relative', rootClassName)}>
      <Trigger triggerRef={triggerRef} text={options.find(o => o.value === value)?.label}
        placeholder={placeholder} open={open} disabled={disabled} label={label}
        className={className} onClick={toggle} required={required} empty={value === ''} />
      {open && sheet && (
        <SheetPanel panelRef={panelRef} label={label} view={view} onClose={close} search={search} rows={rows} />
      )}
      {open && popover && <PopoverPanel panelRef={panelRef} place={popover} search={search} rows={rows} />}
    </div>
  )
}
